
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Bell } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export function NotificationSettings() {
  const [isLoading, setIsLoading] = useState(true);
  const [isOptedOut, setIsOptedOut] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const fetchNotificationSettings = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          setIsLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from("user_notifications")
          .select("opted_out")
          .eq("user_id", session.user.id)
          .single();

        if (error) {
          console.error("Error fetching notification settings:", error);
          toast({
            title: "Error",
            description: "Failed to load notification settings",
            variant: "destructive"
          });
        } else if (data) {
          setIsOptedOut(data.opted_out);
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotificationSettings();
  }, [toast]);

  const handleToggle = async () => {
    setIsSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast({
          title: "Error",
          description: "You must be logged in to update notification settings",
          variant: "destructive"
        });
        return;
      }

      const newOptedOutValue = !isOptedOut;
      
      const { error } = await supabase
        .from("user_notifications")
        .update({ opted_out: newOptedOutValue })
        .eq("user_id", session.user.id);

      if (error) {
        console.error("Error updating notification settings:", error);
        toast({
          title: "Error",
          description: "Failed to update notification settings",
          variant: "destructive"
        });
      } else {
        setIsOptedOut(newOptedOutValue);
        toast({
          title: "Success",
          description: newOptedOutValue 
            ? "You have unsubscribed from weekly notifications" 
            : "You have subscribed to weekly notifications",
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Email Notifications
        </CardTitle>
        <CardDescription>
          Manage your email notification preferences
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center p-4">
            <div className="animate-spin h-5 w-5 border-2 border-blue-500 rounded-full border-t-transparent"></div>
          </div>
        ) : (
          <div className="flex items-center justify-between py-2">
            <div>
              <Label htmlFor="weekly-notifications" className="text-base font-medium">
                Weekly Updates
              </Label>
              <p className="text-sm text-gray-500">
                Receive weekly reminders and tips about Easy Parking
              </p>
            </div>
            <Switch
              id="weekly-notifications"
              checked={!isOptedOut}
              onCheckedChange={handleToggle}
              disabled={isSaving}
            />
          </div>
        )}
      </CardContent>
      <CardFooter className="flex flex-col items-start border-t pt-4">
        <p className="text-xs text-gray-500">
          You can change your notification preferences at any time. We respect your privacy and will never share your information with third parties.
        </p>
      </CardFooter>
    </Card>
  );
}
